import type { MouseEvent, ReactNode } from 'react'

import { cn } from '@/ui/lib/utils'

export interface QuantHeaderTab {
  /** Rendered verbatim as the tab's visible and accessible text. */
  label: string
  href: string
  active: boolean
}

export interface QuantHeaderProps {
  tabs: QuantHeaderTab[]
  /** Mono accent kicker, e.g. `echo-quant · strategies read from disk · 2 run slots`. */
  kicker: string
  /** Router-aware navigation supplied by the caller; this component never imports a router. */
  onNavigate: (href: string) => void
  /** Spec 040: optional fixed-size slot right of the kicker (the market feed indicator). */
  feed?: ReactNode
}

/**
 * The Quant tab's 44px header (spec 036 D-1): a hatched 24px square, the
 * `QUANT` wordmark, a truncating accent kicker and right-aligned sub-tab
 * links in the same tab style as `SubjectHeader`. A paper band reading the
 * `--subj-*` scheme vars with fallbacks. Presentational only: each tab is a
 * real `<a href>` whose click is intercepted and handed to `onNavigate`.
 */
export function QuantHeader({ tabs, kicker, onNavigate, feed }: QuantHeaderProps) {
  function handleClick(event: MouseEvent<HTMLAnchorElement>, href: string) {
    // Let modified clicks (new tab, etc.) behave natively.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
    event.preventDefault()
    onNavigate(href)
  }

  return (
    <header className="flex h-[44px] shrink-0 items-stretch gap-3 border-b border-[var(--subj-rule,var(--ink-14))] bg-[var(--subj-bg,var(--paper))] px-6">
      <span
        aria-hidden="true"
        data-quant-mark
        className="h-6 w-6 shrink-0 self-center border border-accent-line [background:repeating-linear-gradient(135deg,var(--subj-hatch,var(--ink-14))_0_3px,transparent_3px_6px)]"
      />
      <h1 className="flex-none self-center font-sans text-[13px] leading-none font-bold tracking-[0.18em] text-[var(--subj-tx,var(--ink))] uppercase">
        Quant
      </h1>
      <span
        className="min-w-0 self-center truncate font-mono text-[9.5px] tracking-[0.2em] text-accent-line uppercase"
        title={kicker}
      >
        {kicker}
      </span>
      {feed}
      <span aria-hidden="true" className="min-w-0 flex-1" />
      <nav aria-label="Quant sections" className="flex shrink-0 items-stretch">
        {tabs.map((tab) => (
          <a
            key={tab.href}
            href={tab.href}
            onClick={(event) => handleClick(event, tab.href)}
            aria-current={tab.active ? 'page' : undefined}
            className={cn(
              'flex items-center border-b-2 border-transparent px-[14px] font-sans text-[11px] font-semibold tracking-[0.2em] uppercase',
              tab.active
                ? 'border-accent-line font-bold text-[var(--subj-tx,var(--ink))]'
                : 'text-[color-mix(in_oklab,var(--subj-tx,var(--ink))_52%,transparent)]',
            )}
          >
            {tab.label}
          </a>
        ))}
      </nav>
    </header>
  )
}
